<template>
  <RsModal
    id="add_authenticator_modal"
    class-name="add-authenticator-modal"
    :model-value="modelValue"
    :size="step === 2 ? 'lg' : ''"
    :no-close-button="step === 3"
    :no-close-on-backdrop="step === 3"
    :no-close-on-esc="step === 3"
    lazy
    @input="onChangeModalVisible"
    @shown="reset"
  >
    <template #modalTitle>
      <div>
        <div class="add-authenticator__step">
          Step {{ step }} of 3
        </div>
        <h5 class="modal-title">
          {{ step === 3 ? 'Save your recovery codes' : 'Add authenticator' }}
        </h5>
      </div>
    </template>

    <!-- Step 1: name the device and confirm the password -->
    <form
      v-if="step === 1"
      @submit.prevent="submitPassword"
    >
      <p class="add-authenticator__intro">
        Give this authenticator a name so you can tell your devices apart, then confirm your password to continue.
      </p>
      <RsFormInput
        v-model.trim="label"
        label="Device Name"
        type="text"
        placeholder="e.g. Work iPhone"
        maxlength="50"
        required
      />
      <p class="add-authenticator__hint">
        Shown in your list of MFA devices
      </p>
      <RsFormInput
        v-model="password"
        label="Current Password"
        type="password"
        required
      />
      <p
        v-if="error"
        class="text-danger mb-0"
      >
        {{ error }}
      </p>
      <!-- Lets Enter submit the step -->
      <button
        type="submit"
        class="d-none"
      />
    </form>

    <!-- Step 2: scan the QR code and confirm a code from the app -->
    <form
      v-else-if="step === 2"
      class="add-authenticator__scan"
      @submit.prevent="submitCode"
    >
      <div class="add-authenticator__qr">
        <img
          :src="setup?.qrCode"
          alt="QR code to scan with your authenticator app"
        >
      </div>
      <div>
        <ol class="add-authenticator__steps">
          <li>Open an authenticator app such as Google Authenticator, Microsoft Authenticator or 1Password.</li>
          <li>Scan this QR code to add Rentsync.</li>
          <li>Enter the 6-digit code the app shows.</li>
        </ol>
        <label class="add-authenticator__label">
          Verification Code<span class="text-danger ml-1">*</span>
        </label>
        <RsFormCodeInput
          ref="codeInput"
          v-model="code"
          :has-error="!!error"
          :disabled="isLoading"
        />
        <p
          v-if="error"
          class="text-danger mt-2 mb-0"
        >
          {{ error }}
        </p>
        <button
          type="submit"
          class="d-none"
        />
      </div>
    </form>

    <!-- Step 3: save the recovery codes, shown only once -->
    <div v-else>
      <RsBanner
        type="warning"
        message="Save these codes somewhere safe. They are shown only once. If you lose your device, each code lets you sign in one time."
      />
      <div class="add-authenticator__codes">
        <span
          v-for="recoveryCode in recoveryCodes"
          :key="recoveryCode"
          class="add-authenticator__code"
        >{{ recoveryCode }}</span>
      </div>
      <div class="add-authenticator__code-actions">
        <RsButton
          variant="secondary"
          hollow
          icon="far fa-copy mr-2"
          @click="copyCodes"
        >
          Copy codes
        </RsButton>
        <RsButton
          variant="secondary"
          hollow
          icon="far fa-download mr-2"
          @click="downloadCodes"
        >
          Download .txt
        </RsButton>
      </div>
      <label class="add-authenticator__saved">
        <input
          v-model="hasSavedCodes"
          type="checkbox"
        >
        I have saved my recovery codes
      </label>
    </div>

    <template #modal-footer>
      <template v-if="step === 1">
        <RsButton
          variant="cancel"
          @click="close"
        >
          Cancel
        </RsButton>
        <RsButton
          :disabled="!label || (!password && !canReuseSetup) || isLoading"
          @click="submitPassword"
        >
          Continue
        </RsButton>
      </template>
      <template v-else-if="step === 2">
        <RsButton
          variant="cancel"
          class="mr-auto"
          :disabled="isLoading"
          @click="goBack"
        >
          Back
        </RsButton>
        <RsButton
          variant="cancel"
          @click="close"
        >
          Cancel
        </RsButton>
        <RsButton
          :disabled="code.length < 6 || isLoading"
          @click="submitCode"
        >
          Verify
        </RsButton>
      </template>
      <RsButton
        v-else
        :disabled="!hasSavedCodes"
        @click="finish"
      >
        Done
      </RsButton>
    </template>
  </RsModal>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import {
  IAuthenticatorSetup, confirmAuthenticatorSetup, getSetupErrorStatus, startAuthenticatorSetup,
} from '@/shared/services/mfa';
import { downloadFile } from '@/shared/helpers/download-file';
import RsFormCodeInput from '@/components/RsFormCodeInput.vue';

/**
 * Copies text to the clipboard. The Clipboard API only exists on https (and localhost),
 * so fall back to a hidden textarea on plain-http environments like the review apps.
 * @returns whether the text was copied
 */
const copyText = async (text: string): Promise<boolean> => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Fall through to the textarea fallback.
  }
  const area = Object.assign(document.createElement('textarea'), { value: text, readOnly: true });
  area.style.cssText = 'position:fixed;opacity:0;';
  document.body.appendChild(area);
  area.select();
  const copied = document.execCommand('copy');
  area.remove();
  return copied;
};

export default defineComponent({
  name: 'RsModalAddAuthenticator',

  components: { RsFormCodeInput },

  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['input', 'update:modelValue', 'added'],

  data() {
    return {
      step: 1 as 1 | 2 | 3,
      label: '',
      password: '',
      code: '',
      setup: null as IAuthenticatorSetup | null,
      // Name the current setup was started with, so Back + Continue can reuse it.
      setupLabel: '',
      hasSavedCodes: false,
      isLoading: false,
      error: '',
    };
  },

  computed: {
    recoveryCodes(): string[] {
      return this.setup?.codes ?? [];
    },
    // Starting again would create a second pending device with the same (unique) name.
    canReuseSetup(): boolean {
      return !!this.setup && this.setupLabel === this.label;
    },
  },

  methods: {
    reset() {
      this.step = 1;
      this.label = '';
      this.password = '';
      this.code = '';
      this.setup = null;
      this.setupLabel = '';
      this.hasSavedCodes = false;
      this.isLoading = false;
      this.error = '';
    },

    onChangeModalVisible(value: boolean) {
      this.$emit('input', value);
      this.$emit('update:modelValue', value);
    },

    close() {
      this.onChangeModalVisible(false);
    },

    goToScanStep() {
      this.step = 2;
      this.$nextTick(() => (this.$refs.codeInput as InstanceType<typeof RsFormCodeInput> | undefined)?.focus());
    },

    async submitPassword() {
      if (this.canReuseSetup) {
        this.goToScanStep();
        return;
      }
      if (!this.label || !this.password || this.isLoading) return;

      this.error = '';
      this.isLoading = true;
      try {
        this.setup = await startAuthenticatorSetup({ password: this.password, label: this.label });
        this.setupLabel = this.label;
        this.password = '';
        this.goToScanStep();
      } catch (err) {
        const status = getSetupErrorStatus(err);
        if (status === 401) {
          this.error = 'That password is incorrect.';
        } else if (status === 400) {
          // Names stay taken even after a device is removed, so don't say the user still has it.
          this.error = 'That device name has already been used. Choose a different name.';
        } else {
          this.error = 'We couldn\'t start setting up your authenticator. Please try again.';
        }
      } finally {
        this.isLoading = false;
      }
    },

    async submitCode() {
      if (!this.setup || this.code.length < 6 || this.isLoading) return;

      this.error = '';
      this.isLoading = true;
      try {
        await confirmAuthenticatorSetup({ id: this.setup.id, code: this.code });
        this.$emit('added');
        if (this.recoveryCodes.length) {
          this.step = 3;
        } else {
          this.$eventBus.emit('show-success-toast', { message: 'Authenticator added.' });
          this.close();
        }
      } catch (err) {
        this.error = getSetupErrorStatus(err) === 401
          ? 'That code didn\'t match. Check the time on your device and try the newest code.'
          : 'We couldn\'t check that code. Please try again.';
        this.code = '';
        this.$nextTick(() => (this.$refs.codeInput as InstanceType<typeof RsFormCodeInput> | undefined)?.focus());
      } finally {
        this.isLoading = false;
      }
    },

    goBack() {
      this.step = 1;
      this.code = '';
      this.error = '';
    },

    async copyCodes() {
      const copied = await copyText(this.recoveryCodes.join('\n'));
      if (copied) {
        this.$eventBus.emit('show-success-toast', { message: 'Recovery codes copied.' });
        return;
      }
      this.$eventBus.emit('show-error-toast', { message: 'Couldn\'t copy the codes. Use Download .txt instead.' });
    },

    downloadCodes() {
      const text = `Rentsync recovery codes\nEach code can be used once.\n\n${this.recoveryCodes.join('\n')}\n`;
      const url = URL.createObjectURL(new Blob([ text ], { type: 'text/plain' }));
      downloadFile(url, 'rentsync-recovery-codes.txt');
      // Revoking straight away can cancel the download in some browsers.
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    },

    finish() {
      this.$eventBus.emit('show-success-toast', { message: 'Authenticator added.' });
      this.close();
    },
  },
});
</script>

<style lang="scss">
// Opens on top of Account Settings: sit above it and dim it, as a second backdrop would.
.rs-modal.add-authenticator-modal {
  z-index: 1060;
  background: rgba(34, 26, 64, 0.5);
}
</style>

<style lang="scss" scoped>
@import '@/styles/_variables.scss';

.add-authenticator__step {
  margin-bottom: 2px;
  font-size: 0.8125rem;
  font-weight: 700;
  color: $secondary-color;
}
.add-authenticator__intro {
  margin-bottom: 1.25rem;
  color: $tertiary-muted;
}
.add-authenticator__hint {
  margin: -0.5rem 0 1rem;
  font-size: 0.8125rem;
  color: $tertiary-muted;
}
.add-authenticator__scan {
  display: grid;
  grid-template-columns: 216px 1fr;
  gap: 1.75rem;
  align-items: start;
}
// The large modal only widens from Bootstrap's lg breakpoint; stack below it.
@media (max-width: 991px) {
  .add-authenticator__scan {
    grid-template-columns: 1fr;
  }
  .add-authenticator__qr {
    width: 216px;
    margin: 0 auto;
  }
}
.add-authenticator__qr {
  padding: 0.5rem;
  border: 2px solid $border-color;
  border-radius: 6px;
  img {
    display: block;
    width: 100%;
  }
}
.add-authenticator__steps {
  margin: 0 0 1.25rem;
  padding-left: 1.25rem;
  line-height: 1.7;
  color: $tertiary-dark;
}
.add-authenticator__label {
  margin-bottom: 0.5rem;
  font-weight: 700;
  color: $tertiary-dark;
}
.add-authenticator__codes {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.5rem;
  margin: 1.25rem 0 1rem;
}
.add-authenticator__code {
  padding: 0.625rem 0.75rem;
  border-radius: 6px;
  background: $tertiary-light-gray;
  font-family: ui-monospace, Menlo, monospace;
  font-size: 0.9375rem;
  letter-spacing: 0.5px;
  color: $tertiary-dark;
}
.add-authenticator__code-actions {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1.25rem;
  // RsButton title-cases labels, which would turn ".txt" into ".Txt".
  :deep(.rs-button) {
    text-transform: none;
  }
}
.add-authenticator__saved {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  margin: 0;
  color: $tertiary-dark;
  cursor: pointer;
  input {
    width: 18px;
    height: 18px;
    accent-color: $primary-color;
  }
}
</style>
